import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, loading } = useCart();
  const navigate = useNavigate();

  const subtotal = cart.total || 0;
  const tax = subtotal * 0.08;
  const shipping = subtotal > 50 ? 0 : 9.99;
  const total = subtotal + tax + shipping;

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center">
          <ShoppingBag className="h-24 w-24 text-stone-300 mx-auto mb-4" />
          <h2 className="text-3xl font-black text-stone-900 mb-4">Your Cart is Empty</h2>
          <p className="text-stone-600 mb-8">Start adding some amazing phone covers!</p>
          <Link to="/shop/iphone-cases">
            <Button className="bg-amber-600 hover:bg-amber-700 text-lg px-8 rounded-none" data-testid="start-shopping-btn">
              Start Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="cart-page">
      {/* Page Header */}
      <div className="bg-stone-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 40px, #fbbf24 40px, #fbbf24 42px),
              repeating-linear-gradient(-45deg, transparent, transparent 40px, #dc2626 40px, #dc2626 42px)
            `
          }}
        ></div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <span className="text-amber-400 text-sm tracking-[0.2em] uppercase">Your Selection</span>
          </div>
          <h1 className="text-4xl font-black text-white">Shopping Cart</h1>
        </div>
        <div className="h-2"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, 
              #78350f 0px, #78350f 30px, 
              #dc2626 30px, #dc2626 60px, 
              #f59e0b 60px, #f59e0b 90px)`
          }}
        ></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div key={item.product_id} className="bg-white p-6 shadow-lg border-l-4 border-amber-600 flex gap-6" data-testid={`cart-item-${item.product_id}`}>
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-32 h-32 object-cover"
                />
                <div className="flex-1">
                  <Link to={`/product/${item.slug}`} className="hover:text-amber-600 transition-colors">
                    <h3 className="font-bold text-lg mb-2 text-stone-900">{item.name}</h3>
                  </Link>
                  <p className="text-2xl font-black text-stone-900 mb-4">${item.price}</p>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border-2 border-stone-300">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        disabled={loading}
                        className="p-2 hover:bg-stone-100"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="px-4 py-2 font-semibold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        disabled={loading}
                        className="p-2 hover:bg-stone-100"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product_id)}
                      disabled={loading}
                      className="text-red-600 hover:text-red-700 flex items-center gap-2 font-medium"
                      data-testid={`remove-${item.product_id}`}
                    >
                      <Trash2 className="h-5 w-5" />
                      Remove
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-black text-stone-900">${item.subtotal.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 shadow-lg sticky top-8 border-t-4 border-amber-600" data-testid="order-summary">
              <h2 className="text-2xl font-black mb-6 text-stone-900">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-stone-600">Subtotal</span>
                  <span className="font-semibold text-stone-900">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-600">Tax (8%)</span>
                  <span className="font-semibold text-stone-900">${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-600">Shipping</span>
                  <span className="font-semibold text-stone-900">
                    {shipping === 0 ? <span className="text-green-600">FREE</span> : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                {subtotal < 50 && subtotal > 0 && (
                  <p className="text-sm text-amber-600 bg-amber-50 p-2">
                    Add ${(50 - subtotal).toFixed(2)} more for free shipping!
                  </p>
                )}
                <div className="border-t border-stone-200 pt-4">
                  <div className="flex justify-between text-xl font-black">
                    <span className="text-stone-900">Total</span>
                    <span className="text-stone-900">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full bg-amber-600 hover:bg-amber-700 text-lg py-6 rounded-none"
                size="lg"
                data-testid="checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Link to="/shop/iphone-cases" className="block text-center mt-4 text-amber-600 hover:underline font-medium">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
